// src/lib/billing.ts

// --- Firebase & Firestore Importer ---
import { db } from "../firebase"; // Justera sökväg vid behov
import {
  doc,
  collection,
  addDoc,
  updateDoc,
  serverTimestamp,
  Timestamp, // <-- Lades till för Item-typen nedan
} from "firebase/firestore";

// --- Lokala Importer ---
import { REPORTS_COLLECTION, INVOICE_SUBCOLLECTION } from "./reports"; // Se till att denna fil finns och exporterar konstanterna

// === Typer ===

// Minimal Item-typ (definiera mer utförligt eller importera från t.ex. schema.ts)
// Denna inkluderar fälten som generateInvoiceReportForMarkedItems faktiskt använder
type Item = {
  id: string;
  completed?: boolean;
  markedForInvoice?: boolean;
  invoiceReportId?: string | null;
  customerId?: string | null; // Stabilt ID
  customer?: string | null;   // Visningsnamn
  // Lägg till fält som buildInvoiceSummary behöver:
  completionChoice?: "reused" | "resold" | "scrapped" | "scrap" | string | null | undefined;
  reuse?: boolean;
  resold?: boolean;
  scrap?: boolean;
  // Lägg till andra fält som kan behövas av buildInvoiceSummary eller andra funktioner
  [key: string]: any; // Tillåt andra fält
};

// Typ för Firestore-datum (kopierad från Running.txt)
type FirestoreDate = string | Date | Timestamp | null | undefined;

export type BillingSteps = {
  f3Procedure: 0 | 1;
  endpointRemoval: 0 | 1;
  osReinstall: 0 | 1;
  endpointWipe: 0 | 1;
  postWipeBootTest: 0 | 1;
  dataErasure: 0 | 1;
  refurbish: 0 | 1;
};

export type InvoiceSummary = {
  totalItems: number;
  reusedCount: number;
  resoldCount: number;
  scrappedCount: number;
};

// Typ för buildInvoiceSummary input (anpassad från din befintliga)
type MinimalItemForSummary = Pick<Item, 'completionChoice' | 'reuse' | 'resold' | 'scrap'>;


// === Single Source of Truth: computeBillingSteps ===
export function computeBillingSteps(opts: { reuse?: boolean; resold?: boolean; scrap?: boolean }): BillingSteps {
  const { reuse, resold, scrap } = opts || {};

  if (reuse) {
    return { /* ... som innan ... */
        f3Procedure: 0,
        endpointRemoval: 1,
        osReinstall: 1,
        endpointWipe: 0,
        postWipeBootTest: 0,
        dataErasure: 0,
        refurbish: 1,
     };
  }
  if (resold) {
    return { /* ... som innan ... */
        f3Procedure: 0,
        endpointRemoval: 1,
        osReinstall: 0,
        endpointWipe: 1,
        postWipeBootTest: 0,
        dataErasure: 1,
        refurbish: 1,
     };
  }
  if (scrap) {
    return { /* ... som innan ... */
        f3Procedure: 0,
        endpointRemoval: 1,
        osReinstall: 0,
        endpointWipe: 1,
        postWipeBootTest: 0,
        dataErasure: 1,
        refurbish: 0,
     };
  }
  // Default om inget statusflagga satt
  return { /* ... som innan ... */
    f3Procedure: 0,
    endpointRemoval: 0,
    osReinstall: 0,
    endpointWipe: 0,
    postWipeBootTest: 0,
    dataErasure: 0,
    refurbish: 0,
   };
}

// === Summering för rapportdokument ===
export function buildInvoiceSummary(items: MinimalItemForSummary[]): InvoiceSummary {
  let reusedCount = 0;
  let resoldCount = 0;
  let scrappedCount = 0;

  for (const it of items || []) {
    const c = (it.completionChoice || "").toString().toLowerCase();
    const isReuse = it.reuse === true || c === "reused";
    const isResold = it.resold === true || c === "resold";
    const isScrap = it.scrap === true || c === "scrapped" || c === "scrap";

    if (isReuse) reusedCount++;
    else if (isResold) resoldCount++;
    else if (isScrap) scrappedCount++;
  }

  return {
    totalItems: items?.length ?? 0,
    reusedCount,
    resoldCount,
    scrappedCount,
  };
}

// ---- Normalisering av legacy summary-nycklar ----
export type ReportSummaryLike = Partial<{ /* ... som innan ... */
  totalItems: number;
  reusedCount: number;
  resoldCount: number;
  scrappedCount: number;
  reused: number;
  resold: number;
  scrap: number;
  total: number;
  totalAmount: number;
 }>;

export function normalizeSummaryCounts(s: ReportSummaryLike | null | undefined) {
    const src = s || {};
    const totalItems =
      (typeof src.totalItems === "number" ? src.totalItems : undefined) ??
      (typeof src.total === "number" ? src.total : 0);

    const reusedCount =
      (typeof src.reusedCount === "number" ? src.reusedCount : undefined) ??
      (typeof src.reused === "number" ? src.reused : 0);

    const resoldCount =
      (typeof src.resoldCount === "number" ? src.resoldCount : undefined) ??
      (typeof src.resold === "number" ? src.resold : 0);

    const scrappedCount =
      (typeof src.scrappedCount === "number" ? src.scrappedCount : undefined) ??
      (typeof src.scrap === "number" ? src.scrap : 0);

    return { totalItems, reusedCount, resoldCount, scrappedCount };
}


// ===== Fakturering: skapa rapport från markerade poster (ID-baserad kund) =====
export async function generateInvoiceReportForMarkedItems( // <-- EXPORT TILLAGD
  allCompletedItems: Item[],
  currentUserEmail: string | null
): Promise<{ reportId: string; name: string; count: number; customer: string }> {
  // 1) Filtrera markerade och inte redan fakturerade
  const marked = allCompletedItems.filter(
    (it) => it.completed && it.markedForInvoice && !it.invoiceReportId
  );
  if (marked.length === 0) {
    throw new Error("Inga markerade objekt hittades.");
  }

  // 2) ID-baserad kundkontroll: exakt EN customerId
  const customerIds = Array.from(new Set(marked.map((it) => String(it.customerId || "")).filter(Boolean)));
  if (customerIds.length !== 1) {
    throw new Error("Endast en kund per rapport. Justera dina markeringar (kund-ID).");
  }
  const customerId = customerIds[0];

  // 3) Ta fram visningsnamn för rapporttiteln (fallback till ID om namn saknas)
  const firstForCustomer = marked.find((it) => String(it.customerId || "") === customerId);
  const customerName = (firstForCustomer?.customer && String(firstForCustomer.customer).trim()) || customerId;

  // 4) Namn: "Kund YYMMDDHHMM"
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const name = `${customerName} ${String(now.getFullYear()).slice(2)}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}`;

  // 5) Summering (använder buildInvoiceSummary från *denna* fil)
  const summary = buildInvoiceSummary(marked);

  // 6) Skriv rapporten (spara både customerId och customerName för spårbarhet)
  const reportsParent = doc(db, REPORTS_COLLECTION, "root");
  const reportsCol = collection(reportsParent, INVOICE_SUBCOLLECTION);
  const reportRef = await addDoc(reportsCol, {
    name,
    customer: customerName,      // behåll visningsnamn (bakåtkompatibelt)
    customerId,                  // ✅ NYTT: stabilt ID
    createdAt: serverTimestamp(),
    createdBy: currentUserEmail,
    itemIds: marked.map((m) => m.id),
    summary,
  });

  const reportId = reportRef.id;

  // 7) Uppdatera alla berörda items (rensa markering, sätt koppling + tidsstämpel)
  const updates = marked.map((it) =>
    updateDoc(doc(db, "itInventory", it.id), {
      markedForInvoice: false,
      invoiceReportId: reportId,
      invoicedAt: serverTimestamp(),
      stateSort: 2,
      // permanentlyLocked: true, // om du använder detta
    })
  );
  await Promise.all(updates);

  // Returnera kundens visningsnamn (signaturen förväntar 'customer: string')
  return { reportId, name, count: marked.length, customer: customerName };
}
